import {
  CompanyContactDto,
  CompanyLocationDto,
  DeliveryLocationDto,
  MolsonPriceCmdCustomersApiService
} from '@api/customers'
import { handleCatchError } from '@common/utils'
import { successToast } from '@components/reusable'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

type CustomerId = number | string | null | undefined

/**
 * Shared query keys for customer-related queries.
 * Used across multiple modules (customers, quotation-tool, etc.)
 */
export const CUSTOMER_KEYS = {
  // biome-ignore lint/style/useNamingConvention: Query key constant
  CUSTOMER_BY_ID: 'customerById',
  // biome-ignore lint/style/useNamingConvention: Query key constant
  CUSTOMER_DETAILS: 'customerDetails',
  // biome-ignore lint/style/useNamingConvention: Query key constant
  CUSTOMER_DUPLICATES: 'customerDuplicates',
  // biome-ignore lint/style/useNamingConvention: Query key constant
  CUSTOMER_MASTER_RECORD: 'customerMasterRecord'
}

export const useGetActiveCustomer = (cId?: number) => {
  const { id: customerId } = useParams()

  const effectiveCustomerId = cId ?? Number(customerId)

  return useSuspenseQuery({
    queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, Number(effectiveCustomerId)],
    queryFn: async () => {
      if (!effectiveCustomerId || Number.isNaN(effectiveCustomerId)) {
        return {}
      }

      try {
        return await MolsonPriceCmdCustomersApiService.getCustomerById(
          effectiveCustomerId
        )
      } catch (error) {
        handleCatchError(error)
        return {}
      }
    },
    staleTime: 30 * 60 * 1000
  })
}

/**
 * Mutation hook to set a contact as the default contact for a customer.
 * Invalidates the customer query to refresh data.
 *
 * @param customerId - The customer ID
 * @returns Mutation hook with mutateAsync function
 */
export const useSetCustomerDefaultContact = ({
  customerId
}: {
  customerId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ contactId }: { contactId: number }) => {
      return await MolsonPriceCmdCustomersApiService.setDefaultContactDetailsForCustomer(
        customerId,
        contactId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('contactDetailsSetAsDefault') ?? '')

      // Invalidate all variations of customer queries
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

/**
 * Mutation hook to delete a contact from a customer.
 * Requires a newContactId to transfer data to before deletion.
 * Invalidates the customer query to refresh data.
 *
 * @param customerId - The customer ID
 * @param contactId - The contact ID to delete
 * @returns Mutation hook with mutateAsync function that accepts newContactId
 */
export const useDeleteCustomerContactDetails = ({
  customerId,
  contactId
}: {
  customerId: number
  contactId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ newContactId }: { newContactId: number }) => {
      return await MolsonPriceCmdCustomersApiService.deleteContactDetailsFromCustomer(
        customerId,
        contactId,
        newContactId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('contactDetailsRemoved') ?? '')

      // Invalidate all variations of customer queries
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

/**
 * Mutation hook to set a delivery location as default for a customer.
 * Invalidates customer queries to refresh data.
 *
 * @param customerId - The customer ID
 * @returns Mutation hook with mutateAsync function that accepts locationId
 */
export const useSetDefaultCompanyDeliveryLocation = ({
  customerId
}: {
  customerId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ locationId }: { locationId: number }) => {
      return await MolsonPriceCmdCustomersApiService.setDefaultDeliveryLocationForCustomer(
        customerId,
        locationId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('deliveryAddressSetAsDefault') ?? '')

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

/**
 * Mutation hook to set a billing address as default for a customer.
 * Invalidates customer queries to refresh data.
 *
 * @param customerId - The customer ID
 * @returns Mutation hook with mutateAsync function that accepts addressId
 */
export const useSetDefaultCompanyBillingAddress = ({
  customerId
}: {
  customerId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ addressId }: { addressId: number }) => {
      return await MolsonPriceCmdCustomersApiService.setDefaultBillingAddressForCompany(
        customerId,
        addressId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('billingAddressSetAsDefault') ?? '')

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

/**
 * Mutation hook to delete a delivery location from a customer.
 * Requires a newLocationId to transfer quotes/orders to before deletion.
 * Invalidates customer queries to refresh data.
 *
 * @param customerId - The customer ID
 * @param locationId - The location ID to delete
 * @returns Mutation hook with mutateAsync function that accepts newLocationId
 */
export const useDeleteCustomerDeliveryLocation = ({
  customerId,
  locationId
}: {
  customerId: number
  locationId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ newLocationId }: { newLocationId: number }) => {
      return await MolsonPriceCmdCustomersApiService.deleteDeliveryLocationFromCustomer(
        customerId,
        locationId,
        newLocationId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('deliveryAddressRemoved') ?? '')

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

/**
 * Mutation hook to delete a billing address from a customer.
 * Requires a newAddressId to transfer quotes/orders to before deletion.
 * Invalidates customer queries to refresh data.
 *
 * @param customerId - The customer ID
 * @param addressId - The address ID to delete
 * @returns Mutation hook with mutateAsync function that accepts newAddressId
 */
export const useDeleteCustomerBillingAddress = ({
  customerId,
  addressId
}: {
  customerId: number
  addressId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'common' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ newAddressId }: { newAddressId: number }) => {
      return await MolsonPriceCmdCustomersApiService.deleteBillingAddressFromCustomer(
        customerId,
        addressId,
        newAddressId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('billingAddressRemoved') ?? '')

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useUpdateCustomerDeliveryLocation = (customerId: number) => {
  const { t } = useTranslation('', { keyPrefix: 'companyLocation' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: DeliveryLocationDto) => {
      return await MolsonPriceCmdCustomersApiService.updateDeliveryLocationInCustomer(
        customerId,
        payload
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('deliveryLocationUpdated'))

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useAddCustomerDeliveryLocation = (customerId: number) => {
  const { t } = useTranslation('', { keyPrefix: 'companyLocation' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: DeliveryLocationDto) => {
      return await MolsonPriceCmdCustomersApiService.addDeliveryLocationToCustomer(
        customerId,
        payload
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('deliveryLocationAdded'))

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useAddCustomerContactDetails = (customerId: CustomerId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contactDetails: CompanyContactDto) => {
      if (customerId) {
        return await MolsonPriceCmdCustomersApiService.addContactDetailsToCustomer(
          Number(customerId),
          contactDetails
        )
      }
    },
    onSuccess: () => {
      // Ensure we use Number(customerId) to match the query key format
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, Number(customerId)]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, Number(customerId)]
      })
    },
    onError: error => {
      handleCatchError(error)
    }
  })
}

export const useUpdateCustomerContactDetails = (customerId: CustomerId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (contactDetails: CompanyContactDto) => {
      if (customerId) {
        return await MolsonPriceCmdCustomersApiService.updateContactDetailsInCustomer(
          Number(customerId),
          contactDetails
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, Number(customerId)]
      })
    },
    onError: error => {
      handleCatchError(error)
    }
  })
}

export const useAddCompanyBillingAddress = (customerId: CustomerId) => {
  const { t } = useTranslation('', { keyPrefix: 'companyLocation' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CompanyLocationDto) => {
      return await MolsonPriceCmdCustomersApiService.addBillingAddressToCompany(
        payload
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('billingAddressAdded'))

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, Number(customerId)]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, Number(customerId)]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useUpdateCustomerBillingAddress = (customerId: CustomerId) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      addressId,
      addressDetails
    }: {
      addressId: number
      addressDetails: CompanyLocationDto
    }) => {
      if (customerId) {
        return await MolsonPriceCmdCustomersApiService.updateBillingAddressToCompany(
          addressId,
          addressDetails
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_BY_ID, Number(customerId)]
      })
      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_KEYS.CUSTOMER_DETAILS, Number(customerId)]
      })
    },
    onError: error => {
      handleCatchError(error)
    }
  })
}
